import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin } from "lucide-react";
import { getLocalPage, LOCAL_PAGES } from "../data/localPages";
import { SERVICES } from "../data/services";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, Label } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const SITE_URL = "https://resineko.com";

const LocalPage = ({ slug }) => {
  const page = getLocalPage(slug);
  if (!page) return null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: `${page.serviceName || "Revêtements de sols"} à ${page.city}`,
        provider: { "@type": "LocalBusiness", name: "Resineko", telephone: "+33783693101", address: { "@type": "PostalAddress", addressLocality: "Daux", postalCode: "31700", addressCountry: "FR" } },
        areaServed: page.city,
        description: page.metaDescription,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "Zone d'intervention", item: `${SITE_URL}/zone-intervention` },
          { "@type": "ListItem", position: 3, name: page.h1, item: `${SITE_URL}/${page.slug}` },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: page.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
    ],
  };

  const other = LOCAL_PAGES.find((p) => p.slug !== slug);

  return (
    <>
      <Seo title={page.metaTitle} description={page.metaDescription} path={`/${page.slug}`} jsonLd={jsonLd} />
      <PageHero kicker={page.kicker} title={page.h1} subtitle={page.heroSub} image={page.image} />

      {/* Contexte local */}
      <section className="noise-bg bg-[#0A0A0A] py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 md:grid-cols-12 md:px-10">
          <div className="md:col-span-5">
            <Reveal><Label>Secteur {page.city}</Label></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] text-[#F4F4F0] md:text-5xl">{page.contextTitle}</h2>
            </Reveal>
          </div>
          <div className="space-y-6 md:col-span-6 md:col-start-7">
            {page.context.map((p, i) => (
              <Reveal key={i} delay={0.15 + i * 0.05}>
                <p className="text-sm leading-relaxed text-[#8C8C8C] md:text-base">{p}</p>
              </Reveal>
            ))}
            <Reveal delay={0.25}>
              <a href="tel:+33783693101" data-testid="local-phone-cta" className="group inline-flex items-center gap-3 text-sm font-medium text-[#2FBF8F]">
                <Phone size={15} /> 07 83 69 31 01 — 7h30 à 18h30
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Savoir-faire disponibles localement */}
      <section className="bg-[#0e0e0e] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal><Label>Nos savoir-faire à {page.city}</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-2xl font-serif text-3xl font-light leading-tight text-[#F4F4F0] md:text-4xl">
              Six solutions de revêtement, disponibles près de chez vous
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.05}>
                <Link
                  to={`/${s.slug}`}
                  data-testid={`local-service-${s.slug}`}
                  className="group flex items-center justify-between gap-4 border border-white/10 bg-[#111] p-6 transition-colors duration-300 hover:border-[#047857]"
                >
                  <div>
                    <h3 className="font-serif text-xl font-light text-[#F4F4F0] transition-colors group-hover:text-[#2FBF8F]">{s.name}</h3>
                    <p className="mt-1 text-xs text-[#8C8C8C]">{s.tagline}</p>
                  </div>
                  <ArrowRight size={16} className="shrink-0 text-[#8C8C8C] transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#2FBF8F]" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Réalisations */}
      <section className="bg-[#0A0A0A] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal><Label>Réalisations Resineko</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl font-light text-[#F4F4F0] md:text-4xl">Quelques-uns de nos chantiers</h2>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {page.gallery.map((img, i) => {
              const block = (
                <div className="group overflow-hidden border border-white/10">
                  <img src={img.src} alt={img.alt} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
              );
              return (
                <Reveal key={img.src} delay={i * 0.06}>
                  {img.to ? (
                    <Link to={img.to} data-testid={`local-gallery-link-${i}`} className="block">
                      {block}
                    </Link>
                  ) : block}
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.15}>
            <Link to="/realisations" data-testid="local-realisations-link" className="group mt-10 inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.22em] text-[#2FBF8F]">
              Voir toutes nos réalisations <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FAQ locale */}
      <section className="bg-[#0e0e0e] py-20 md:py-28" data-testid="local-faq">
        <div className="mx-auto max-w-4xl px-5 md:px-10">
          <Reveal><Label>Questions fréquentes — {page.city}</Label></Reveal>
          <Reveal delay={0.15}>
            <Accordion type="single" collapsible className="mt-8">
              {page.faq.map((f, i) => (
                <AccordionItem key={i} value={`lfaq-${i}`} className="border-white/10">
                  <AccordionTrigger data-testid={`local-faq-trigger-${i}`} className="text-left font-serif text-lg font-normal text-[#F4F4F0] hover:text-[#2FBF8F] hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-[#8C8C8C]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Maillage interne */}
      <section className="border-t border-white/10 bg-[#0A0A0A] py-14">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-5 md:px-10">
          <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#8C8C8C]">
            <MapPin size={13} className="text-[#2FBF8F]" /> Secteurs
          </span>
          <Link to="/zone-intervention" data-testid="local-link-zone" className="border border-white/15 px-4 py-2 text-xs text-[#F4F4F0]/75 transition-colors hover:border-[#047857] hover:text-[#2FBF8F]">
            Toute la France
          </Link>
          {other && (
            <Link to={`/${other.slug}`} data-testid="local-link-other" className="border border-white/15 px-4 py-2 text-xs text-[#F4F4F0]/75 transition-colors hover:border-[#047857] hover:text-[#2FBF8F]">
              {other.h1}
            </Link>
          )}
        </div>
      </section>

      <CTASection
        title={page.ctaTitle || `Un projet de sol à ${page.city} ?`}
        text={page.ctaText || "Décrivez-nous votre projet : nous vous orientons vers le revêtement adapté et vous établissons un devis personnalisé."}
      />
    </>
  );
};

export default LocalPage;
